import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FamilyCampPageOptions } from '../Constants/RegistrationPageOptions';
import { RoomTier } from '../Constants/FamilyCampConfig';
import DataAccessHelper from '../Helper/DataAccessHelper';
import FamilyCampWeekSelection from '../Components/FamilyCampWeekSelection';
import FamilyCampTierSelection from '../Components/FamilyCampTierSelection';
import FamilyCampPersonalInfo from '../Components/FamilyCampPersonalInfo';
import MedicalInsuranceInformation from '../Components/MedicalInsuranceInformation';
import Disclaimer from '../Components/Disclaimer';
import FamilyCampRegistrationReview from '../Components/FamilyCampRegistrationReview';
import { useCart } from '../Contexts/CartContext';
import '../Styles/RetreatRegistration.scss';

// Family Camp is the one overnight flow with no "join a group" step — families register as their
// own unit — and no direct-pay step either: it only ever lands in the cart, same as the Review
// page's single "Add to Cart" button implies. That drops the amountDue/paymentAmount/paymentType
// state and the ProcessRegistration/AddTransaction machinery every other wizard carries for its
// Payment page, since there is no Payment page here.
function FamilyCampRegistration() {

   const navigate = useNavigate();
   const { refreshCount } = useCart();
   const [account, setAccount] = useState();
   const [selectedPage, setSelectedPage] = useState(FamilyCampPageOptions.WeekSelection);
   const [week, setWeek] = useState();
   const [roomType, setRoomType] = useState(RoomTier.Standard);
   const [signatureData, setSignatureData] = useState({ signature: '', signatureDate: null });

   const [personalInformation, setPersonalInformation] = useState({
      firstName: '',
      lastName: '',
      gender: '',
      strAddress: '',
      strAddress2: '',
      city: '',
      state: '',
      zip: '',
      phone: '',
      email: '',
      ecName: '',
      ecRelationship: '',
      ecPhone: '',
      birthDate: '',
      familyMembers: [{ firstName: '', lastName: '', birthDate: '' }]
   });

   const [medicalInformation, setMedicalInformation] = useState({
      insuranceCompany: '',
      insuranceId: '',
      groupPolicyNumber: '',
      policyHolderName: '',
      policyHolderBirthdate: null,
      hasDiabetes: 'off',
      hasHeartCondition: 'off',
      hasEpilepsy: 'off',
      hasEnvironmentAllergies: 'off',
      hasHeatExhaustion: 'off',
      hasThyroidProblem: 'off',
      hasLupus: 'off',
      hasHighBloodPressure: 'off',
      hasAsthma: 'off',
      drugAllergies: '',
      emergencyAction1: '',
      foodAllergies: '',
      emergencyAction2: '',
      otherAllergies: '',
      emergencyAction3: '',
      medicationName: '',
      medicationDose: '',
      tetanusShotDate: null
   });

   async function setAccountForCurrentGuest() {
      const accountResponse = await DataAccessHelper.getCurrentAccountInformation();
      setAccount(accountResponse.account);
   }

   useEffect(() => {
      setAccountForCurrentGuest();
   }, []);

   // Puts the finished registration in the cart. /cart/add stores this verbatim and /cart/quote
   // (and, at checkout, insertFamilyCamp) reprice the family member roster live from
   // services/cart/pricing.js's ladder — nothing here decides money.
   async function AddToCart() {
      try {
         const response = await fetch('/cart/add', {
            method: 'POST',
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
               flow: 'family',
               payload: {
                  accountId: account.account_id,
                  week,
                  signatureData: {
                     ...signatureData,
                     signatureDate: signatureData.signatureDate ?? new Date().toISOString(),
                  },
                  roomType,
                  personalInformation,
                  medicalInformation,
               },
            })
         });

         const data = await response.json()
         if (!response.ok) {
            return data.error ?? 'Could not add this registration to your cart.'
         }

         refreshCount()
         navigate('/mycart')
         return ''
      } catch (error) {
         console.log(error)
         return 'Could not add this registration to your cart.'
      }
   }

   return (
      <div className="retreatRegistrationContainer">
         {selectedPage === FamilyCampPageOptions.WeekSelection &&
            <FamilyCampWeekSelection
               nextPage={FamilyCampPageOptions.TierSelection}
               setSelectedPage={setSelectedPage}
               setWeekSelection={w => setWeek(w)}
            />
         }
         {selectedPage === FamilyCampPageOptions.TierSelection &&
            <FamilyCampTierSelection
               week={week}
               setRoomType={setRoomType}
               selectedPage={selectedPage}
               setSelectedPage={setSelectedPage}
               previousPage={FamilyCampPageOptions.WeekSelection}
               nextPage={FamilyCampPageOptions.PersonalInfo}
            />
         }
         {selectedPage === FamilyCampPageOptions.PersonalInfo &&
            <FamilyCampPersonalInfo
               week={week}
               roomType={roomType}
               personalInformation={personalInformation}
               setPersonalInformation={setPersonalInformation}
               selectedPage={selectedPage}
               setSelectedPage={setSelectedPage}
               previousPage={FamilyCampPageOptions.TierSelection}
               nextPage={FamilyCampPageOptions.MedicalInsurance}
            />
         }
         {selectedPage === FamilyCampPageOptions.MedicalInsurance &&
            <MedicalInsuranceInformation
               medicalInformation={medicalInformation}
               setMedicalInformation={setMedicalInformation}
               selectedPage={selectedPage}
               setSelectedPage={setSelectedPage}
               previousPage={FamilyCampPageOptions.PersonalInfo}
               nextPage={FamilyCampPageOptions.Disclaimer}
            />
         }
         {selectedPage === FamilyCampPageOptions.Disclaimer &&
            <Disclaimer
               week={week}
               signatureData={signatureData}
               setSignatureData={setSignatureData}
               selectedPage={selectedPage}
               setSelectedPage={setSelectedPage}
               nextPage={FamilyCampPageOptions.Review}
               previousPage={FamilyCampPageOptions.MedicalInsurance}
            />
         }
         {selectedPage === FamilyCampPageOptions.Review &&
            <FamilyCampRegistrationReview
               week={week}
               roomType={roomType}
               personalInformation={personalInformation}
               setSelectedPage={setSelectedPage}
               AddToCart={AddToCart}
            />
         }
      </div>
   )
}

export default FamilyCampRegistration;
